import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  sin, cos, mix, smoothstep, clamp, max, abs,
  positionLocal, positionPrevious, positionWorld,
  normalize, negate, texture
} from 'three/tsl';
import { cell2 } from './tsl/noise';

// Moon surface for the "Boxing on the Moon" map. Three responsibilities:
//
//   1. Real vertex displacement — the plane humps up into a gently rolling
//      terrain via a static sum-of-sines heightfield (borrowed from the
//      sun map's lava, but frozen in time: `waveSpeed = 0` by design).
//   2. Cratering — multi-scale Worley-profile craters stacked on top of
//      the waves so every patch of visible ground has crater rims, ejecta,
//      and deep shadowed interiors.
//   3. Albedo — sampled from the moonsurface PBR set, projected planar
//      against world XZ at a configurable tile scale. Crater rim ejecta
//      brightens it, deep crater interiors darken it, and the procedural
//      crater + wave gradient drives a hand-rolled directional shade
//      multiplier (the material runs with `lights = false` because the
//      crater shading we want would never come out of standard lambert).
//
// Ring keep-out mask is preserved from the lava version: displacement
// fades to zero near the ring footprint so tall crater rims can't poke
// through the ring base.
export interface MoonGroundMaterialOpts {
  /** moonsurface basecolor map. Required — the procedural mare/highland
   *  palette is gone; this texture is the albedo. */
  colorMap: THREE.Texture;
  /** Optional heightmap, sampled via finite differences inside the analytic
   *  shading path so it perturbs the surface normal in addition to the
   *  procedural waves + craters. Vertex displacement is left untouched —
   *  this is the shading-only "relief" path. */
  heightMap?: THREE.Texture;
}

export class MoonGroundMaterial extends NodeMaterial {
  readonly uTime = uniform(0); // unused for motion; retained for API parity

  // Static displacement: how tall the rolling wave terrain gets.
  readonly uWaveAmp = uniform(1.4);
  // Crater displacement multipliers per scale band. Large craters dominate
  // the silhouette; medium/small add detail. Each is peak rim-to-floor
  // amplitude in metres.
  readonly uCraterLarge  = uniform(1.8);
  readonly uCraterMedium = uniform(0.55);
  readonly uCraterSmall  = uniform(0.14);

  // Ring keep-out (matches lava mask — axis-aligned square using max(|x|,|z|)).
  readonly uWaveRingMaskStart = uniform(8.0);
  readonly uWaveRingMaskEnd   = uniform(11.0);

  // Albedo tile scale: tiles per metre of world XZ. 0.05 → 1 tile per 20m.
  readonly uTileScale = uniform(0.05);

  // Heightmap → bump relief amplitude. Multiplies the heightmap's local
  // gradient before it's added to the procedural wave + crater gradient
  // that drives the analytic shading. Zero verts moved; just nudges the
  // shading normal. Default 0 → no-op without a heightMap.
  readonly uHeightBumpScale = uniform(0);

  // Crater-relief colour stops. uShadow darkens deep interiors; uEjecta
  // brightens the rim ring on the slope-detected ejecta highlight.
  readonly uShadow = uniform(new THREE.Vector3(0.05, 0.05, 0.07));
  readonly uEjecta = uniform(new THREE.Vector3(0.92, 0.90, 0.86));

  // Fake-sun direction — matches Renderer.ts:48 DirectionalLight, same as
  // LavaMaterial. Toggle `normalSign` if shadows light the wrong slopes
  // on a visual check (per the verify-directions discipline).
  readonly uSunDir = uniform(new THREE.Vector3(-0.47, 0.80, 0.40));
  readonly uShadeStrength = uniform(0.95);
  readonly uNormalSign = uniform(1);
  // Soft floor on the shade term. The moon has no atmosphere to back-fill
  // shadows, so crater interiors can go nearly black — but zero kills the
  // silhouette entirely. 0.04 keeps the darkest shadows readable.
  readonly uAmbient = uniform(0.04);

  constructor(opts: MoonGroundMaterialOpts) {
    super();
    this.lights = false;

    // ----- Static wave heightfield (sum-of-sines) ---------------------------
    // Same shape as LavaMaterial.lavaWaves but the time argument is zeroed
    // so the waves are frozen — rolling terrain, not a churning surface.
    // Returns vec3(height, dH/dx, dH/dz) in world units, pre-uWaveAmp.
    const waveField = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const dirs = [
        { x:  0.9578, y:  0.2873, lambda: 28, A: 0.30 },
        { x: -0.5145, y:  0.8575, lambda: 16, A: 0.17 },
        { x:  0.5812, y: -0.8137, lambda:  9, A: 0.08 }
      ];
      let height: Node<'float'> = float(0);
      let gx:     Node<'float'> = float(0);
      let gz:     Node<'float'> = float(0);
      for (const w of dirs) {
        const k = (2 * Math.PI) / w.lambda;
        const d = vec2(w.x, w.y);
        const phase = p.dot(d).mul(k);
        const s = sin(phase);
        const c = cos(phase);
        height = height.add(s.mul(w.A));
        gx = gx.add(c.mul(w.A * k * w.x));
        gz = gz.add(c.mul(w.A * k * w.y));
      }
      return vec3(height, gx, gz);
    });

    // ----- Crater profile ---------------------------------------------------
    // Given normalized distance-to-nearest-feature d (roughly 0..1 in cell
    // units), returns a single scalar heightfield contribution:
    //
    //   d ∈ [0.00, 0.35]: deep bowl (negative, parabolic)
    //   d ∈ [0.35, 0.50]: rising flank
    //   d ∈ [0.50, 0.60]: rim bump (positive peak near d=0.53)
    //   d ∈ [0.60,  ∞ ]: flat
    //
    // The shape is handcrafted from smoothsteps rather than an exponential
    // so the profile is continuous and vanishes exactly at d=0.6 — no
    // long tail that would give the ground a lumpy base level everywhere.
    const craterProfile = Fn((args: [Node<'float'>]) => {
      const [d] = args;
      // Inside-bowl depression: maxes at d=0, fades out by d=0.35.
      const t = smoothstep(0.35, 0.0, d);
      const depression = t.mul(t).mul(0.9);
      // Rim bump: narrow positive peak around d≈0.50.
      const rim = smoothstep(0.38, 0.50, d).mul(smoothstep(0.62, 0.50, d));
      return rim.mul(0.35).sub(depression);
    });

    // Sum of crater contributions at three scales. Worley cells are
    // 1-unit wide in their sampling space, so the frequencies here set the
    // real-world crater sizes: 0.025 → ~40 m, 0.09 → ~11 m, 0.28 → ~3.5 m.
    const craterField = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;

      const L = cell2(p.mul(0.025));
      const M = cell2(p.mul(0.09).add(vec2(101.3, 77.1)));
      const S = cell2(p.mul(0.28).add(vec2(9.7, 42.5)));

      const hL = craterProfile(L.x).mul(this.uCraterLarge);
      const hM = craterProfile(M.x).mul(this.uCraterMedium);
      const hS = craterProfile(S.x).mul(this.uCraterSmall);
      return hL.add(hM).add(hS);
    });

    // Ring keep-out mask — same formulation as LavaMaterial.
    const ringMask = Fn((args: [Node<'vec2'>]) => {
      const [worldXZ] = args;
      const d = max(abs(worldXZ.x), abs(worldXZ.y));
      return smoothstep(this.uWaveRingMaskStart, this.uWaveRingMaskEnd, d);
    });

    // ----- Vertex displacement ---------------------------------------------
    // Same mesh convention as the lava plane: authored in local XY, rotated
    // x=-π/2 so local +Z maps to world +Y. World-XZ sampling coord =
    // (localX, -localY).
    // Vertex displacement. `positionPrevious.assign(displaced)` keeps the
    // velocity MRT from treating our static displacement as per-frame
    // motion — three's default VelocityNode compares `positionLocal`
    // (which the next line makes the displaced value) against
    // `positionPrevious` (raw `positionGeometry` attribute by default),
    // and the non-zero difference reads as a constant smear. Matching the
    // pattern three.js itself uses in SkinningNode/InstanceNode for the
    // same reason: vertex-level position changes that aren't real motion.
    this.positionNode = Fn(() => {
      const worldXZ = vec2(positionLocal.x, negate(positionLocal.y));
      const wave = waveField(worldXZ);
      const crater = craterField(worldXZ);
      const mask = ringMask(worldXZ);
      const h = wave.x.mul(this.uWaveAmp).add(crater).mul(mask);
      const displaced = positionLocal.add(vec3(0, 0, h));
      positionPrevious.assign(displaced);
      return displaced;
    })();

    this.colorNode = Fn(() => {
      const p0 = positionWorld.xz.toVar();

      // ----- Albedo from the moonsurface PBR ------------------------------
      // World-projected planar UV: tile scale is in tiles-per-metre so the
      // texture density is independent of the plane's geometry size and
      // tessellation. The mare/highland procedural palette and the dust
      // sparkle that used to live here are gone — the texture provides
      // both the colour variation and the regolith micro-detail.
      const albedoUV = p0.mul(this.uTileScale);
      const baseColor = texture(opts.colorMap, albedoUV).rgb.toVar();

      // ----- Crater-relief shading -----------------------------------------
      // Analytic gradient for the static wave field comes free (waveField
      // returns dH/dx, dH/dz). The crater field has no closed-form gradient
      // (Worley has discontinuities at cell boundaries), so we difference
      // it numerically with a small epsilon. The extra cost is ~2×cell2, i.e.
      // 18 hashes per sample — acceptable for a pass that runs only where
      // the moon ground is visible.
      const wave = waveField(p0);
      const eps = float(0.5); // metres — small vs ~3 m smallest crater
      const hC  = craterField(p0).toVar();
      const hCx = craterField(p0.add(vec2(eps, 0)));
      const hCz = craterField(p0.add(vec2(0, eps)));
      const dCdx = hCx.sub(hC).div(eps);
      const dCdz = hCz.sub(hC).div(eps);

      // Combined gradient (waves + craters). Crater gradient is already in
      // world-space because craterField samples world-XZ directly.
      let totalDHdxN = wave.y.mul(this.uWaveAmp).add(dCdx);
      let totalDHdzN = wave.z.mul(this.uWaveAmp).add(dCdz);

      // Heightmap-driven shading relief. Optional. Sampled with the same
      // tile scale as the albedo (so colour and bump line up), differenced
      // numerically across `eps` metres of world space, multiplied by
      // `uHeightBumpScale` and added to the procedural gradient. Pure
      // shading — no verts pushed.
      if (opts.heightMap) {
        const tileEps = eps.mul(this.uTileScale);
        const baseUV = p0.mul(this.uTileScale);
        const hT  = texture(opts.heightMap, baseUV).r;
        const hTx = texture(opts.heightMap, baseUV.add(vec2(tileEps, float(0))));
        const hTz = texture(opts.heightMap, baseUV.add(vec2(float(0), tileEps)));
        const dTdx = hTx.r.sub(hT).div(eps);
        const dTdz = hTz.r.sub(hT).div(eps);
        totalDHdxN = totalDHdxN.add(dTdx.mul(this.uHeightBumpScale));
        totalDHdzN = totalDHdzN.add(dTdz.mul(this.uHeightBumpScale));
      }
      const totalDHdx = totalDHdxN;
      const totalDHdz = totalDHdzN;

      const nSign = this.uNormalSign;
      const n = normalize(vec3(
        negate(totalDHdx).mul(nSign),
        float(1),
        negate(totalDHdz).mul(nSign)
      ));
      const sunDir = normalize(this.uSunDir);
      const ndl = max(n.dot(sunDir), float(0));
      const shade = max(ndl.mul(this.uShadeStrength), this.uAmbient);

      // ----- Crater-rim ejecta highlight -----------------------------------
      // Bright fresh-ejecta ring around each crater rim. Detected by the
      // gradient magnitude of the crater field — gentler slopes = flat
      // terrain = no brightening, steeper slopes = rim/wall = ejecta.
      const slope = dCdx.mul(dCdx).add(dCdz.mul(dCdz)).sqrt();
      const rimBright = smoothstep(0.35, 1.1, slope);
      const ejectaMix = rimBright.mul(0.45);
      const colorWithEjecta = mix(baseColor, this.uEjecta, ejectaMix);

      // Darken deep crater interiors (negative height relative to base
      // terrain) — pure geometric shading already does most of the work,
      // but this reinforces the "shadowed pit" feel even from overhead.
      const depth = clamp(negate(hC), 0, 1.2);
      const depthDarken = smoothstep(0.15, 1.1, depth);
      const colorShaded = mix(colorWithEjecta, this.uShadow, depthDarken.mul(0.55));

      // Compose. Texture albedo × crater modulations × directional shade.
      const body = colorShaded.mul(shade);
      return vec4(body, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
